import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { type Theme, useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";

import { getBackendAPI } from "../../../api/apiConfig";
import { normalizePayer } from "../../../api/filters/claimFilters";
import { WieldyStatus } from "../../../status";
import {
  formatDateTime,
  formatDaysUntilAppointment,
} from "../../../utils/dateUtils";
import { enumToStrings, preserveUrlParams } from "../../../utils/utils";
import type { CellParams, DataTableColDef } from "../../DataTable";
import { LiveRowNote } from "../../LiveSync/LiveRowMarks";
import DownloadSource from "../../PDFViewer/DownloadSource";
import { hasFailed, PostedState } from "../../posting";
import { verificationReason } from "../utils/statusDetail";
import PostButton from "./PostButton";

type IVCellParams = CellParams<InsuranceVerificationMessage>;
type IVColDef = DataTableColDef<InsuranceVerificationMessage>;

const formatActorName = (value: string | null | undefined): string => {
  if (!value) return "-";
  const name = value.includes("@") ? value.split("@")[0] : value;
  return name.replace(/[._-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const followUpLabels = new Map<string, { label: string }>([
  ["WAITING_ON_PATIENT", { label: "Waiting on patient" }],
  ["INFORMATION_ADDED", { label: "Information added" }],
  ["MANUALLY_RESOLVED", { label: "Manually resolved" }],
]);

// Helper function to determine ledger entry color based on content
const getPolicyNotesColor = (policyNotes: string) => {
  if (!policyNotes)
    return { bg: "grey.50", border: "grey.200", text: "text.primary" };

  // Red for no coverage cases
  if (
    policyNotes.includes("No Ortho Coverage") ||
    policyNotes.includes("No Active Ortho Coverage")
  ) {
    return { bg: "#ffebee", border: "#ffcdd2", text: "#d32f2f" };
  }

  // Green for active coverage with benefits (contains lifetime max, percentages, etc.)
  if (
    policyNotes.includes("Lifetime max") ||
    policyNotes.includes("Plan pays")
  ) {
    return { bg: "#e8f5e8", border: "#c8e6c9", text: "#2e7d32" };
  }

  // Yellow for any other cases (intermediary)
  return { bg: "#fff3e0", border: "#ffcc02", text: "#ef6c00" };
};

const verificationStatusColor: (value: string, theme: Theme) => string = (
  value,
  theme,
) => {
  switch (value) {
    case "QUEUED":
      return theme.palette.info.main;
    case "IN_PROGRESS":
    case "REVIEW":
    case "REVERIFY":
      return theme.palette.warning.main;
    case "COMPLETED":
      return theme.palette.success.main;
    case "SKIPPED":
      return theme.palette.info.main;
    case "ARCHIVED":
      return theme.palette.info.main;
    case "MISSED":
      return theme.palette.error.main;
    default:
      return "#000";
  }
};

const verificationOutcomeColor: (value: string, theme: Theme) => string = (
  value,
  theme,
) => {
  switch (value) {
    case "COVERAGE_ACTIVE_HAS_ORTHO":
      return theme.palette.success.main;
    case "COVERAGE_ACTIVE_NO_ORTHO":
      return theme.palette.error.main;
    case "COVERAGE_INACTIVE":
      return theme.palette.error.main;
    case "INSUFFICIENT_DATA":
      return theme.palette.error.main;
    case "CARRIER_UNAVAILABLE":
      return theme.palette.error.main;
    case "PATIENT_NOT_FOUND":
      return theme.palette.error.main;
    case "ALREADY_VERIFIED":
      return theme.palette.info.main;
    case "NOT_REQUIRED":
      return theme.palette.info.main;
    case "SECONDARY_INSURANCE":
      return theme.palette.info.main;
    default:
      return "#000";
  }
};

const getVerificationOutcomeTooltipContent = (
  outcome: string,
  verification: InsuranceVerificationMessage,
): string => {
  switch (outcome) {
    case "COVERAGE_ACTIVE_HAS_ORTHO":
      return "Active coverage with ortho benefits";
    case "COVERAGE_ACTIVE_NO_ORTHO":
      return "Active coverage, no ortho benefits";
    case "COVERAGE_INACTIVE":
      return "Insurance coverage inactive";
    case "INSUFFICIENT_DATA": {
      return verificationReason(verification) || "Missing required information";
    }
    case "CARRIER_UNAVAILABLE":
      return "Insurance carrier unavailable";
    case "PATIENT_NOT_FOUND":
      return "Patient not found with insurance";
    case "ALREADY_VERIFIED":
      return "Already verified";
    case "NOT_REQUIRED":
      return "Verification not required";
    case "SECONDARY_INSURANCE":
      return "Secondary insurance";
    case "COVERAGE_ACTIVE":
      return "Active coverage";
    default:
      return verificationReason(verification) || "No details available";
  }
};

function VerificationStatusCell({ params }: { params: IVCellParams }) {
  const value = String(params.value ?? "");
  const theme = useTheme();

  const getStatusBackground = (status: string) => {
    switch (status) {
      case "REVIEW":
        return { bg: "#fff3e0", border: "#ffcc02" };
      default:
        return { bg: "#e8f5e8", border: "#d0d5dd" };
    }
  };

  const statusBg = getStatusBackground(value);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          padding: "4px 8px",
          backgroundColor: statusBg.bg,
          borderRadius: "12px",
          border: `1px solid ${statusBg.border}`,
          display: "inline-block",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: verificationStatusColor(value, theme),
            fontWeight: 600,
          }}
        >
          {value}
        </Typography>
      </Box>
      <LiveRowNote id={params.row.wieldyId} />
    </Box>
  );
}

function VerificationOutcomeCell({ params }: { params: IVCellParams }) {
  const { row } = params;
  const value = params.value as string | null;
  const theme = useTheme();

  // Always render as read-only badge on list view
  if (!value || value === "-") {
    return (
      <Typography
        variant="body2"
        sx={{ color: "#475467", fontStyle: "italic" }}
      >
        -
      </Typography>
    );
  }

  const tooltipContent = getVerificationOutcomeTooltipContent(value, row);

  return (
    <Tooltip
      title={
        <Typography variant="body2" sx={{ p: 1 }}>
          {tooltipContent}
        </Typography>
      }
      placement="bottom-start"
      arrow
    >
      <Box
        sx={{
          padding: "4px 8px",
          backgroundColor: "#e8f5e8",
          borderRadius: "12px",
          border: "1px solid #d0d5dd",
          display: "inline-block",
          cursor: "help",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: verificationOutcomeColor(value, theme),
            fontWeight: 600,
          }}
        >
          {value
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (l: string) => l.toUpperCase())}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function renderInsuranceVerificationAction(
  params: IVCellParams,
  updateVerification: (payload: InsuranceVerificationUpdateMessage) => void,
) {
  const rawValue = (params.value as string) ?? "";
  const selectValue = followUpLabels.has(rawValue) ? rawValue : "";
  return (
    <FormControl fullWidth>
      <Select
        displayEmpty
        labelId="iv-action-select-label"
        id="iv-action-simple-select"
        value={selectValue}
        onChange={(e) => {
          const selectedValue = e.target.value as string;
          const verification = params.row;
          updateVerification({
            wieldyId: verification.wieldyId,
            practiceId: verification.practiceId,
            action: selectedValue || null,
          });
        }}
        sx={{
          ".MuiOutlinedInput-notchedOutline": {
            border: 0,
          },
          borderRadius: "12px",
        }}
      >
        <MenuItem value="">-</MenuItem>
        {Array.from(followUpLabels.keys()).map((key) => (
          <MenuItem key={key} value={key}>
            {followUpLabels.get(key)?.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function getDefaultColumns(
  updateVerification: (payload: InsuranceVerificationUpdateMessage) => void,
  postToPMS: (verification: InsuranceVerificationMessage) => void,
  unpost: (verification: InsuranceVerificationMessage) => void,
  markManuallyPosted?: (verification: InsuranceVerificationMessage) => void,
  navigate?: (path: string) => void,
  selectedPractices: PracticeMessage[] = [],
  isDevMode = false,
  fetchScreenshots?: (
    verification: InsuranceVerificationMessage,
  ) => Promise<void>,
  fetchBennyScreenshots?: (
    verification: InsuranceVerificationMessage,
  ) => Promise<void>,
): IVColDef[] {
  const verificationStatuses = ["COMPLETED", "SKIPPED"];
  if (isDevMode) {
    verificationStatuses.push(
      "REVIEW",
      "QUEUED",
      "IN_PROGRESS",
      "ARCHIVED",
      "MISSED",
      "NOT_APPLICABLE",
      "REVERIFY",
    );
  }
  const defaultColumns: IVColDef[] = [
    {
      field: "appointmentDatetime",
      headerName: "Appointment Datetime",
      flex: 1,
      type: "date",
      valueGetter: (value) => (value ? new Date(value as string) : null),
      renderCell: (params: IVCellParams) =>
        params.row.appointmentDatetime
          ? formatDateTime(params.row.appointmentDatetime)
          : "",
    },
    {
      field: "daysUntilAppointment",
      headerName: "Days Until",
      flex: 0.7,
      type: "number",
      filterable: false,
      valueFormatter: (value) => formatDaysUntilAppointment(value as number),
    },
    {
      field: "practiceDisplayName",
      headerName: "Practice",
      flex: 1.2,
      filterable: false,
      valueGetter: (_value, row) => {
        const practice = selectedPractices.find(
          (p) => p.wieldyId === row.practiceId,
        );
        return (
          practice?.displayName ||
          practice?.name ||
          row.locationInfo?.practiceName ||
          "-"
        );
      },
    },
    {
      field: "appointmentLocation",
      headerName: "Location",
      flex: 1,
    },
    {
      field: "patientName",
      headerName: "Patient Name",
      flex: 1.2,
      renderCell: (params: IVCellParams) => {
        const { patientKey, patientName } = params.row;

        if (patientKey) {
          return (
            <Link
              to={`/patients/${patientKey}`}
              style={{
                textDecoration: "underline",
                color: "inherit",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  textDecoration: "underline",
                }}
              >
                {patientName}
              </Typography>
            </Link>
          );
        }

        return <Typography variant="body2">{patientName}</Typography>;
      },
    },
    {
      field: "patientDob",
      headerName: "Patient DOB",
      flex: 1,
      type: "date",
      valueGetter: (value) => (value ? new Date(value as string) : null),
      valueFormatter: (value) =>
        value
          ? (value as Date).toLocaleDateString(undefined, { timeZone: "UTC" })
          : "",
    },
    {
      field: "insuranceCompany",
      headerName: "Insurance",
      flex: 1.2,
    },
    {
      field: "policyNumber",
      headerName: "Policy #",
      flex: 1,
    },
    {
      field: "networkStatus",
      headerName: "Network Status",
      flex: 1,
      valueGetter: (_value, row) => {
        const networkStatus = row.locationInfo?.networkStatus;
        if (!networkStatus) return "Unavailable";
        switch (networkStatus) {
          case "IN_NETWORK":
            return "In Network";
          case "OUT_OF_NETWORK":
            return "Out of Network";
          default:
            return "Unavailable";
        }
      },
      renderCell: (params: IVCellParams) => {
        const networkStatus = params.row.locationInfo?.networkStatus;

        let displayValue: string;
        let color: string;

        switch (networkStatus) {
          case "IN_NETWORK":
            displayValue = "In Network";
            color = "#2e7d32";
            break;
          case "OUT_OF_NETWORK":
            displayValue = "Out of Network";
            color = "#d32f2f";
            break;
          default:
            displayValue = "Unavailable";
            color = "#757575";
        }

        return (
          <Typography
            variant="body2"
            sx={{
              color,
              fontWeight: networkStatus ? 600 : 400,
              fontStyle: networkStatus ? "normal" : "italic",
            }}
          >
            {displayValue}
          </Typography>
        );
      },
    },
    {
      field: "groupNumber",
      headerName: "Group #",
      flex: 0.8,
    },
    {
      field: "productType",
      headerName: "Product Type",
      flex: 1,
      valueGetter: (_value, row) => row.wieldyProductType || "",
    },
    {
      field: "verificationStatus",
      headerName: "Status",
      flex: 1,
      type: "singleSelect",
      valueOptions: verificationStatuses,
      renderCell: (params: IVCellParams) => (
        <VerificationStatusCell params={params} />
      ),
    },
    {
      field: "verificationOutcome",
      headerName: "Outcome",
      flex: 1.2,
      type: "singleSelect",
      valueOptions: [
        "COVERAGE_ACTIVE",
        "COVERAGE_INACTIVE",
        "INSUFFICIENT_DATA",
        "TECHNICAL_ERROR",
        "CARRIER_UNAVAILABLE",
        "PATIENT_NOT_FOUND",
        "ALREADY_VERIFIED",
        "SECONDARY_INSURANCE",
        "NOT_REQUIRED",
        "COVERAGE_ACTIVE_HAS_ORTHO",
        "COVERAGE_ACTIVE_NO_ORTHO",
      ],
      renderCell: (params: IVCellParams) => (
        <VerificationOutcomeCell params={params} />
      ),
    },
    {
      field: "wieldyPolicyNotes",
      headerName: "Policy Notes",
      flex: 0.8,
      disableExport: true,
      renderCell: (params: IVCellParams) => {
        const { wieldyPolicyNotes } = params.row;

        if (!wieldyPolicyNotes) {
          return (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              -
            </Typography>
          );
        }

        const colors = getPolicyNotesColor(wieldyPolicyNotes);

        return (
          <Tooltip
            title={
              <Box
                sx={{
                  whiteSpace: "pre-wrap",
                  maxWidth: 400,
                  p: 1,
                  backgroundColor: colors.bg,
                  borderRadius: 1,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.text,
                    fontWeight: 500,
                  }}
                >
                  {wieldyPolicyNotes}
                </Typography>
              </Box>
            }
            placement="bottom-start"
            arrow
          >
            <Box
              sx={{
                cursor: "pointer",
                padding: "6px 8px",
                backgroundColor: "#e3f2fd",
                borderRadius: "12px",
                border: "1px solid #bbdefb",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "24px",
                height: "24px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#1976d2",
                  fontSize: "14px",
                  lineHeight: 1,
                }}
              >
                📝
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "id",
      headerName: "PDF",
      flex: 0.6,
      disableExport: true,
      renderCell: (params: IVCellParams) => {
        const baseUrl = getBackendAPI();
        const url = `${baseUrl}insurance-verification/${params.row.wieldyId}/form.pdf`;
        return (
          <DownloadSource url={url} downloadFileName={`${params.row.id}.pdf`} />
        );
      },
    },
    {
      field: "action",
      headerName: "Follow-Up",
      flex: 1,
      type: "singleSelect",
      valueOptions: [
        { value: "null", label: "None" },
        { value: "WAITING_ON_PATIENT", label: "Waiting on patient" },
        { value: "INFORMATION_ADDED", label: "Information added" },
        { value: "MANUALLY_RESOLVED", label: "Manually resolved" },
      ],
      renderCell: (params: IVCellParams) =>
        renderInsuranceVerificationAction(params, updateVerification),
      valueGetter: (_value, row) => {
        const action = row.action;
        return action && followUpLabels.has(action) ? action : "null";
      },
    },
  ];

  // Dev mode only columns
  const devModeOnlyColumns: IVColDef[] = [
    {
      field: "firstRetrieved",
      headerName: "Appointment Retrieved",
      flex: 1,
      type: "date",
      valueGetter: (value) => (value ? new Date(value as string) : null),
      valueFormatter: (value) =>
        value ? (value as Date).toLocaleDateString() : "",
    },
    {
      field: "Details",
      headerName: "Details",
      flex: 0.8,
      disableExport: true,
      renderCell: (params: IVCellParams) => {
        if (!navigate) {
          return null;
        }

        // Show "Edit" button for completed verifications, "Work on This" for queued
        const isCompleted =
          params.row.verificationStatus === "COMPLETED" ||
          params.row.verificationStatus === "SKIPPED" ||
          params.row.verificationStatus === "NOT_APPLICABLE";

        if (
          ["QUEUED", "REVERIFY"].includes(params.row.verificationStatus ?? "")
        ) {
          return (
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(
                  preserveUrlParams(
                    `/insurance-verification/${params.row.wieldyId}`,
                  ),
                )
              }
              sx={{
                minWidth: "auto",
                textTransform: "none",
                fontSize: "0.75rem",
                padding: "4px 8px",
              }}
            >
              Work on This
            </Button>
          );
        }

        if (params.row.verificationStatus === "REVIEW") {
          return (
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(
                  preserveUrlParams(
                    `/insurance-verification/${params.row.wieldyId}`,
                  ),
                )
              }
              sx={{
                minWidth: "auto",
                textTransform: "none",
                fontSize: "0.75rem",
                padding: "4px 8px",
              }}
            >
              Verify
            </Button>
          );
        }

        if (isCompleted) {
          return (
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                navigate(
                  preserveUrlParams(
                    `/insurance-verification/${params.row.wieldyId}`,
                  ),
                )
              }
              sx={{
                minWidth: "auto",
                textTransform: "none",
                color: "primary.main",
                borderColor: "primary.main",
                fontSize: "0.75rem",
                padding: "4px 8px",
              }}
            >
              Modify
            </Button>
          );
        }

        return null;
      },
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1.5,
      type: "date",
      renderCell: (params: IVCellParams) =>
        params.row.updatedAt
          ? `${new Date(params.row.updatedAt).toLocaleDateString()} ${new Date(
              params.row.updatedAt,
            ).toLocaleTimeString(navigator.language, {
              hour: "2-digit",
              minute: "2-digit",
            })}`
          : "-",
      valueGetter: (value) => (value ? new Date(value as string) : null),
    },
    {
      field: "postedState",
      headerName: "Posted State",
      flex: 1,
      type: "singleSelect",
      valueOptions: enumToStrings(PostedState).map((postedState) => ({
        value: postedState.toUpperCase(),
        label: postedState,
      })),
      renderCell: (params: IVCellParams) => (
        <PostButton
          isPosting={false}
          onPost={() => postToPMS(params.row)}
          onManualPost={() => {
            if (markManuallyPosted) {
              markManuallyPosted(params.row);
            }
          }}
          postedState={params.row.postedState}
          verificationStatus={params.row.verificationStatus}
          postedLatestErrorMessage={params.row.postedLatestErrorMessage}
        />
      ),
    },
    {
      field: "unpostAction",
      headerName: "Unpost",
      flex: 0.8,
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params: IVCellParams) => {
        const verification = params.row;
        const isPosted =
          verification.postedState === PostedState[PostedState.AUTO_POSTED] ||
          verification.postedState ===
            PostedState[PostedState.MANUALLY_POSTED] ||
          verification.postedState === PostedState[PostedState.FAILURE];

        if (!isDevMode || !isPosted) {
          return (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              -
            </Typography>
          );
        }

        return (
          <Button
            type="button"
            variant="contained"
            size="small"
            onClick={() => unpost(verification)}
            sx={{
              cursor: "pointer",
              minWidth: "70px",
            }}
          >
            Unpost
          </Button>
        );
      },
    },
    {
      field: "postedByActor",
      headerName: "Posted By",
      flex: 1,
      renderCell: (params: IVCellParams) => (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {formatActorName(params.row.postedByActor)}
        </Typography>
      ),
    },
    {
      field: "verifiedBy",
      headerName: "Verified By",
      flex: 1,
      renderCell: (params: IVCellParams) => (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {formatActorName(params.row.verifiedBy)}
        </Typography>
      ),
    },
    {
      field: "approvedBy",
      headerName: "Approved By",
      flex: 1,
      renderCell: (params: IVCellParams) => (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {formatActorName(params.row.approvedBy)}
        </Typography>
      ),
    },
    {
      field: "approvedAt",
      headerName: "Approved At",
      flex: 1.5,
      type: "date",
      renderCell: (params: IVCellParams) =>
        params.row.approvedAt
          ? `${new Date(params.row.approvedAt).toLocaleDateString()} ${new Date(
              params.row.approvedAt,
            ).toLocaleTimeString(navigator.language, {
              hour: "2-digit",
              minute: "2-digit",
            })}`
          : "-",
      valueGetter: (value) => (value ? new Date(value as string) : null),
    },
    {
      field: "postedDateTime",
      headerName: "Posted Date",
      flex: 1.5,
      type: "date",
      renderCell: (params: IVCellParams) =>
        params.row.postedDateTime
          ? `${new Date(params.row.postedDateTime).toLocaleDateString()} ${new Date(
              params.row.postedDateTime,
            ).toLocaleTimeString(navigator.language, {
              hour: "2-digit",
              minute: "2-digit",
            })}`
          : "-",
      valueGetter: (value) => (value ? new Date(value as string) : null),
    },
    {
      field: "verifiedAt",
      headerName: "Verification Date",
      flex: 1.5,
      type: "date",
      renderCell: (params: IVCellParams) =>
        params.row.verifiedAt
          ? `${new Date(params.row.verifiedAt).toLocaleDateString()} ${new Date(
              params.row.verifiedAt,
            ).toLocaleTimeString(navigator.language, {
              hour: "2-digit",
              minute: "2-digit",
            })}`
          : "-",
      valueGetter: (value) => (value ? new Date(value as string) : null),
    },
    {
      field: "screenshot",
      headerName: "Screenshot",
      flex: 0.8,
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params: IVCellParams) => {
        const verification = params.row;
        if (!fetchScreenshots) {
          return (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              -
            </Typography>
          );
        }

        // Show success icon for auto-posted verifications
        if (verification.postedState === PostedState[PostedState.AUTO_POSTED]) {
          return (
            <CheckCircleIcon
              color="success"
              onClick={async () => {
                await fetchScreenshots(verification);
              }}
              sx={{ cursor: "pointer" }}
            />
          );
        }

        // Show error icon for failed verifications
        if (hasFailed(verification)) {
          return (
            <ErrorOutlineIcon
              color="error"
              onClick={async () => {
                await fetchScreenshots(verification);
              }}
              sx={{ cursor: "pointer" }}
            />
          );
        }

        return (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            -
          </Typography>
        );
      },
    },
    {
      field: "bennyScreenshot",
      headerName: "Benny",
      flex: 0.8,
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params: IVCellParams) => {
        const verification = params.row;
        if (!fetchBennyScreenshots) {
          return (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              -
            </Typography>
          );
        }

        const bennyStatus = verification.bennyRetrievalStatus;

        if (bennyStatus === "SUCCESS") {
          return (
            <CheckCircleIcon
              color="success"
              onClick={async () => {
                await fetchBennyScreenshots(verification);
              }}
              sx={{ cursor: "pointer" }}
            />
          );
        }

        if (bennyStatus === "FAILURE") {
          return (
            <ErrorOutlineIcon
              color="error"
              onClick={async () => {
                await fetchBennyScreenshots(verification);
              }}
              sx={{ cursor: "pointer" }}
            />
          );
        }

        return (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            -
          </Typography>
        );
      },
    },
    {
      field: "payerName",
      headerName: "Payer Name",
      flex: 1,
      renderCell: (params: IVCellParams) => (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {params.row.payerName ? normalizePayer(params.row.payerName) : ""}
        </Typography>
      ),
    },
    {
      flex: 1,
      field: "wieldyStatus",
      headerName: "Wieldy Status",
      type: "singleSelect",
      valueOptions: enumToStrings(WieldyStatus).map((wieldyStatus) => ({
        value: wieldyStatus.toUpperCase(),
        label: wieldyStatus,
      })),
      renderCell: (params: IVCellParams) => (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {params.row.wieldyStatus}
        </Typography>
      ),
    },
  ];

  // Add dev mode columns conditionally
  if (isDevMode) {
    defaultColumns.push(...devModeOnlyColumns);
  }

  return defaultColumns;
}

// Default column visibility model
export function getDefaultColumnVisibility(isDevMode = false) {
  if (isDevMode) {
    // Dev mode: All columns available, 11 visible by default
    return {
      appointmentDatetime: true,
      daysUntilAppointment: true,
      appointmentLocation: true,
      patientName: true,
      insuranceCompany: true,
      policyNumber: true,
      networkStatus: true,
      verificationStatus: true,
      verificationOutcome: true,
      approvedBy: true,
      approvedAt: true,
      wieldyPolicyNotes: true,
      id: true, // PDF
      action: false,
      Details: true,

      // Standard columns available but hidden by default in dev mode
      practiceDisplayName: false,
      patientDob: false,
      groupNumber: false,
      productType: false,

      // Dev mode additional columns (available but not visible by default)
      firstRetrieved: false,
      updatedAt: false,
      postedState: true,
      postedByActor: false,
      verifiedBy: false,
      postedDateTime: false,
      verifiedAt: true,
      screenshot: true,
      bennyScreenshot: false,
      unpostAction: true, // UNPOST column visible in dev mode
    };
  }

  // Non-dev mode: Customer columns only, 11 visible by default
  return {
    appointmentDatetime: true,
    daysUntilAppointment: true,
    practiceDisplayName: false,
    appointmentLocation: true,
    patientName: true,
    patientDob: false,
    insuranceCompany: true,
    policyNumber: true,
    networkStatus: true,
    groupNumber: false,
    productType: false,
    verificationStatus: true,
    verificationOutcome: true,
    approvedBy: true,
    approvedAt: true,
    wieldyPolicyNotes: true,
    id: true, // PDF column
    action: true, // Action column
    firstRetrieved: false,
    Details: false,
    updatedAt: false,
    postedState: false,
    postedByActor: false,
    verifiedBy: false,
    postedDateTime: false,
    verifiedAt: false,
    screenshot: false,
    bennyScreenshot: false,
    unpostAction: false, // UNPOST column hidden in non-dev mode
  };
}
